import { createCSV } from "../../src/utils/files";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { push } from "connected-react-router";
import i18next from "config/i18next";
import { ApiResponse, ApiResponseResult } from "middleware/api";
import moment from "moment";
import { PickerFileRequestType } from "pages/detailPicker/types";
import { actions } from "reducers/pickers";
import { NotificationStateType } from "reducers/types/notification";
import {
  call,
  CallEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import {
  ACTIVE_PICKER_STATUS,
  DATE_FORMATS,
  VEHICLE_TAGS,
} from "utils/constants";
import * as pickersMiddleware from "../middleware/pickers";
import {
  EditPickerResponseType,
  ExportParamsType,
  ParamsMiddlewareType,
  PickerFileResponseType,
  PickersAxiosResponseType,
  PickersExportResponseType,
  PickersResponse,
  PickerType,
} from "../pages/pickers/types";
import { actions as detailPickerActions } from "../reducers/detailPicker";
import { actions as sessionActions } from "../reducers/session";
import { actions as notificationActions } from "../reducers/notification";
import {
  AccountTypesApiResponseContentType,
  BankResponseType,
  BankType,
  CsvResponseType,
  DetailPickersIdentificationTypeApiResponseType,
  DetailPickersProvincesApiResponseType,
  PickerFileSaveParamsType,
  PickerResponseType,
  PickersResponseType,
  ProvincesTypes,
} from "./types/pickers";
import { connectionErrorAlert, serverErrorAlert } from "utils/alerts";
import { store } from "../store";
import { FormApi } from "final-form";

const sagas = [
  takeLatest(actions.getPendingUserRequest.type, getPickers),
  takeLatest(actions.getPendingUserExportRequest.type, getPendingUserExport),
  takeLatest(actions.getMorePendingUserRequest.type, getMorePendingUser),
  takeLatest(
    detailPickerActions.getPendingUserPickerRequest.type,
    getPendingUserPicker
  ),
  takeLatest(
    detailPickerActions.getPendingUserPickerExportRequest.type,
    getPendingUserPickerExport
  ),
  takeLatest(
    detailPickerActions.getPendingUserPickerDocumentsEditRequest.type,
    postPendingUserDocumentsEdit
  ),
  takeLatest(detailPickerActions.getAprovePickerRequest.type, postAprovePicker),
  takeLatest(detailPickerActions.getEditPickerRequest.type, postEditPicker),
  takeLatest(detailPickerActions.getPickerFileRequest.type, getPickerFile),
  takeLatest(detailPickerActions.getPickerFileSaveRequest.type, putFileUpload),
  takeLatest(detailPickerActions.getBanksRequest.type, getBanks),
  takeLatest(detailPickerActions.getPickerFileDeleteRequest.type, fileDelete),

  takeLatest(
    actions.getIdentificationTypesRequest.type,
    getIdentificationsType
  ),
  takeLatest(detailPickerActions.getProvincesRequest.type, getProvinces),
  takeLatest(detailPickerActions.getAccountTypeRequest.type, getAccountTypes),
  takeLatest(detailPickerActions.getBankNameFetch.type, getBankName),
];

export default sagas;

const formatPickerDate = (date: string | null) => {
  return moment(date, DATE_FORMATS.shortDate).format(DATE_FORMATS.shortISODate);
};

const formatVehicleDate = (date: string) => {
  return date.match(DATE_FORMATS.regexshortDate)
    ? formatPickerDate(date)
    : date;
};

const process = (body: PickerType): PickerType => {
  const { accountingData, vehicle } = body;
  const { address } = accountingData;
  return {
    ...body,
    desiredWorkzones: body.desiredWorkzones
      ? (body.desiredWorkzones as string).split(",")
      : [],
    personalData: {
      ...body.personalData,
      dateOfBirth: formatPickerDate(body.personalData.dateOfBirth),
    },
    accountingData: {
      ...accountingData,
      fiscalNumber: accountingData.fiscalNumber || null,
      sapInterlocutor:
        accountingData.sapInterlocutor &&
        accountingData.sapInterlocutor !== "-"
          ? accountingData.sapInterlocutor
          : null,
      address: {
        ...address,
        postalCode: address.postalCode || null,
        locality: address.locality || null,
        province: (address.province as ProvincesTypes)?.tag || null,
        street: address.street || null,
        streetNumber: address.streetNumber || null,
      },
    },
    vehicle: {
      ...vehicle,
      patent:
        vehicle?.type === VEHICLE_TAGS.BICYCLE
          ? null
          : vehicle?.patent?.toUpperCase(),
      expirationDatePolicyVehicle:
        vehicle.expirationDatePolicyVehicle &&
        vehicle.expirationDatePolicyVehicle !== ""
          ? formatVehicleDate(vehicle.expirationDatePolicyVehicle)
          : null,
      expirationDateIdentificationVehicle:
        vehicle.expirationDateIdentificationVehicle &&
        vehicle.expirationDateIdentificationVehicle !== ""
          ? formatVehicleDate(vehicle.expirationDateIdentificationVehicle)
          : null,
      expirationDateDriverLicense:
        vehicle.expirationDateDriverLicense &&
        vehicle.expirationDateDriverLicense !== ""
          ? formatVehicleDate(vehicle.expirationDateDriverLicense)
          : null,
    },
  };
};
function* getPickers({
  payload,
}: PayloadAction<ParamsMiddlewareType>): Generator<
  | CallEffect<AxiosResponse<PickersAxiosResponseType>>
  | PutEffect<{ type: string; pendingUsers: PickersResponse }>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<PickersResponseType>
> {
  const response = yield call(pickersMiddleware.getPickers, payload);
  if (response.status !== 200) {
    yield put(notificationActions.showNotification(serverErrorAlert()));
    yield put(actions.getPendingUserError());
  } else {
    const {
      result: { items },
      ...rest
    } = response.data;
    yield put(actions.getPendingUserSuccess({ items, ...rest }));
  }
}

function* getBanks({
  payload,
}: PayloadAction<{ cbuPrefix?: string }>): Generator<
  | CallEffect<AxiosResponse<ApiResponse<BankResponseType>>>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: BankType[]; type: string }>,
  void,
  ApiResponse<BankResponseType>
> {
  const response = yield call(pickersMiddleware.getBanks);
  if (response.status !== 200) {
    yield put(detailPickerActions.getBanksError());
  } else {
    const { result } = response.data;
    yield put(detailPickerActions.getBanksSuccess(result.items));
  }
}

function* getBankName({
  payload,
}: PayloadAction<{ cbuPrefix: string }>): Generator<
  | CallEffect<AxiosResponse<ApiResponseResult<BankType>>>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: BankType; type: string }>,
  void,
  AxiosResponse<ApiResponseResult<BankType>>
> {
  const response = yield call(pickersMiddleware.getBankName, payload.cbuPrefix);
  if (response.status !== 200) {
    yield put(detailPickerActions.getBankNameError());
  } else {
    const { result } = response.data;
    yield put(detailPickerActions.getBankNameSuccess(result));
  }
}

function* getMorePendingUser({
  payload,
}: PayloadAction<ParamsMiddlewareType>): Generator<
  | CallEffect<AxiosResponse<PickersAxiosResponseType>>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<PickersResponseType>
> {
  const response = yield call(pickersMiddleware.getPickers, payload);
  if (response.status !== 200) {
    yield put(actions.getPendingUserError());
  } else {
    const {
      result: { items },
      ...rest
    } = response.data;
    yield put(actions.getMorePendingUserSuccess({ items, ...rest }));
  }
}

function* getPendingUserPicker({
  payload,
}: PayloadAction<number>): Generator<
  CallEffect<AxiosResponse<PickerType>> | PutEffect<{ type: string }>,
  void,
  ApiResponse<PickerResponseType>
> {
  const response = yield call(pickersMiddleware.getPicker, payload);
  if (response.status !== 200) {
    const state = store.getState();
    const { countryCode } = state.session;
    const { dispatch } = store;
    yield put(
      notificationActions.showNotification(
        serverErrorAlert(() => dispatch(push(`/${countryCode}/pickers?tab=${state.pickers.actualPage}`)))
      )
    );
    yield put(detailPickerActions.getPendingUserPickerError());
  } else {
    const { result } = response.data;
    yield put(detailPickerActions.getPendingUserPickerSuccess(result));
    yield put(
      actions.setActualPage(
        ACTIVE_PICKER_STATUS.includes(result.status.tag) ? "pickers" : "pending"
      )
    );
    
    const termsData = result.term?.currentVersion && result.term?.userVersion
      ? {
          currentVersion: result.term.currentVersion,
          userVersion: result.term.userVersion,
          needsUpdate: result.term.needsUpdate || false,
        }
      : null;
    
    yield put(detailPickerActions.setPickerTermsStatus(termsData));
  }
}

function* getPendingUserExport({
  payload,
}: PayloadAction<ParamsMiddlewareType>): Generator<
  | CallEffect<AxiosResponse<PickersExportResponseType>>
  | PutEffect<{ type: string }>,
  void,
  CsvResponseType
> {
  const response = yield call(pickersMiddleware.getPickersExport, payload);

  if (response.status !== 200) {
    yield put(actions.getPendingUserExportError());
  } else {
    createCSV(response.data, "pickers");
    yield put(actions.getPendingUserExportSuccess());
    yield put(
      notificationActions.showNotification({
        id: "notification-export-success",
        level: "success",
        title: i18next.t("alerts:exportTitle"),
        body: i18next.t("alerts:exportMessage"),
      })
    );
  }
}

function* getPendingUserPickerExport({
  payload: { email },
}: PayloadAction<ExportParamsType>): Generator<
  | CallEffect<AxiosResponse<PickersExportResponseType>>
  | PutEffect<{ type: string }>,
  void,
  CsvResponseType
> {
  const response = yield call(pickersMiddleware.getPickersExport, { email });
  if (response.status !== 200) {
    yield put(detailPickerActions.getPendingUserPickerExportError());
  } else {
    createCSV(response.data, "pickers");
    yield put(
      notificationActions.showNotification({
        id: "notification-export-success",
        level: "success",
        title: i18next.t("alerts:exportTitle"),
        body: i18next.t("alerts:exportMessage"),
      })
    );
    yield put(detailPickerActions.getPendingUserPickerExportSuccess());
  }
}

function* validateFiscalNumber(
  scrollTo: () => void,
  scrollToEmail: () => void,
  statusCode?: number
) {
  switch (statusCode) {
    case 10016:
      scrollTo();
      yield put(detailPickerActions.setFiscalDuplicated(true));
      break;
    case 10001:
      scrollToEmail();
      yield put(detailPickerActions.setEmailOrUsernameDuplicated(true));
      break;
    default:
      yield put(notificationActions.showNotification(connectionErrorAlert()));
  }
}

function* postPendingUserDocumentsEdit({
  payload: { params, scrollTo, scrollToEmail, formRef },
}: PayloadAction<{
  params: PickerType;
  scrollTo: () => void;
  scrollToEmail: () => void;
  formRef: React.MutableRefObject<FormApi<object, object> | null>;
}>): Generator<
  | CallEffect<AxiosResponse<EditPickerResponseType>>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | PutEffect<{ type: string }>
  | ReturnType<typeof validateFiscalNumber>,
  void,
  ApiResponse<PickerResponseType>
> {
  let body = process(params);
  const response = yield call(pickersMiddleware.postPickerDocumentsEdit, body);

  if (response.status !== 200) {
    yield validateFiscalNumber(
      scrollTo,
      scrollToEmail,
      response.data.statusCode
    );
    yield put(detailPickerActions.getPendingUserPickerDocumentsEditError());
  } else {
    yield put(
      detailPickerActions.getPendingUserPickerDocumentsEditSuccess(body)
    );
    yield put(notificationActions.hideNotification());
    yield put(detailPickerActions.getPendingUserPickerRequest(body.id));
    yield put(sessionActions.setDirty(false));
    if (formRef.current) {
      formRef.current.initialize(formRef.current.getState().values);
    }
  }
}

function* postAprovePicker({
  payload: { params, goBack, scrollTo, scrollToEmail },
}: PayloadAction<{
  params: PickerType;
  goBack: Function;
  scrollTo: () => void;
  scrollToEmail: () => void;
}>): Generator<
  | CallEffect<AxiosResponse<EditPickerResponseType>>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | PutEffect<{ type: string }>
  | ReturnType<typeof validateFiscalNumber>,
  void,
  ApiResponse<PickerResponseType>
> {
  let body = process(params);
  const response = yield call(pickersMiddleware.postAprovePicker, body);
  if (response.status !== 200 && response.status !== 204) {
    yield validateFiscalNumber(
      scrollTo,
      scrollToEmail,
      response.data.statusCode
    );

    yield put(detailPickerActions.getPendingUserPickerDocumentsEditError());
  } else {
    yield put(
      notificationActions.showNotification({
        id: "notification-approved",
        level: "success",
        title: i18next.t("picker:approvedTitle"),
        body: i18next.t("picker:approvedMessage"),
        onClose: goBack,
      })
    );
    yield put(detailPickerActions.getAprovePickerSuccess(params));
  }
}

function* postEditPicker({
  payload: { params, scrollTo, scrollToEmail, formRef },
}: PayloadAction<{
  params: PickerType;
  scrollTo: () => void;
  scrollToEmail: () => void;
  formRef: React.MutableRefObject<FormApi<object, object> | null>;
}>): Generator<
  | CallEffect<AxiosResponse<EditPickerResponseType>>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | PutEffect<{ type: string }>
  | ReturnType<typeof validateFiscalNumber>,
  void,
  ApiResponse<PickerResponseType>
> {
  let body = process(params);
  const response = yield call(pickersMiddleware.postEditPicker, body);
  if (response.status !== 200) {
    yield validateFiscalNumber(
      scrollTo,
      scrollToEmail,
      response.data.statusCode
    );

    yield put(detailPickerActions.getEditPickerError());
  } else {
    yield put(
      notificationActions.showNotification({
        id: "notification-changes-saved",
        level: "success",
        title: i18next.t("alerts:changesSavedTitle"),
        body: i18next.t("alerts:changesSavedMessage"),
        onConfirmLabel: i18next.t("global:continue"),
      })
    );
    yield put(detailPickerActions.getEditPickerSuccess(body));
    yield put(detailPickerActions.getPendingUserPickerRequest(body.id));
    if (formRef.current) {
      formRef.current.initialize(formRef.current.getState().values);
    }
  }
}

function* getPickerFile({
  payload,
}: PayloadAction<PickerFileRequestType>): Generator<
  | CallEffect<AxiosResponse<PickerFileResponseType>>
  | PutEffect<{ type: string }>,
  void,
  { status: number; data: PickerFileResponseType }
> {
  const response = yield call(pickersMiddleware.getFile, payload);

  if (response.status !== 200) {
    yield put(detailPickerActions.getPickerFileError());
  } else {
    const { result } = response.data;
    if (result.url) window.open(result.url, "_blank");
    yield put(detailPickerActions.getPickerFileSuccess());
  }
}

function* fileDelete({
  payload: { id, tag },
}: PayloadAction<PickerFileRequestType>): Generator<
  PutEffect<{ type: string }> | CallEffect<AxiosResponse<void>> | void,
  void,
  AxiosResponse<void>
> {
  const response = yield call(pickersMiddleware.deleteFile, id, tag);
  if (response.status !== 204) {
    yield put(detailPickerActions.getPickerFileDeleteError(tag));
  } else {
    yield put(detailPickerActions.getPickerFileDeleteSuccess(tag));
    yield put(detailPickerActions.getPendingUserPickerRequest(id));
  }
}

function* putFileUpload({
  payload: { id, ...body },
}: PayloadAction<PickerFileSaveParamsType>): Generator<
  PutEffect<{ type: string }> | CallEffect<AxiosResponse<void>> | void,
  void,
  AxiosResponse<void>
> {
  const response = yield call(pickersMiddleware.fileUpload, id, body);
  if (response.status !== 204) {
    yield put(detailPickerActions.getPickerFileSaveError(body.tag));
  } else {
    yield put(detailPickerActions.getPickerFileSaveSuccess(body.tag));
    yield put(detailPickerActions.getPendingUserPickerRequest(id));
  }
}

function* getProvinces(): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<DetailPickersProvincesApiResponseType>>,
  void,
  DetailPickersProvincesApiResponseType
> {
  const response = yield call(pickersMiddleware.getProvinces);
  if (response.status !== 200) {
    yield put(detailPickerActions.getProvincesError());
  } else {
    const { result } = response.data;

    yield put(detailPickerActions.getProvincesSuccess(result.items));
  }
}

function* getAccountTypes(): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<AccountTypesApiResponseContentType>>>,
  void,
  ApiResponse<AccountTypesApiResponseContentType>
> {
  const response = yield call(pickersMiddleware.getAccountType);
  if (response.status !== 200) {
    yield put(detailPickerActions.getAccountTypeError());
  } else {
    const { result } = response.data;

    yield put(detailPickerActions.getAccountTypeSuccess(result.items));
  }
}

function* getIdentificationsType(): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<DetailPickersIdentificationTypeApiResponseType>>,
  void,
  DetailPickersIdentificationTypeApiResponseType
> {
  const response = yield call(pickersMiddleware.getIdentificationType);
  if (response.status !== 200) {
    yield put(actions.getIdentificationTypesError());
  } else {
    const { result } = response.data;

    yield put(actions.getIdentificationTypesSuccess(result.items));
  }
}
